import { PplFormActionsComponent } from '../form-actions/form-actions.component';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { notFirstChange } from '@ppl/utils/angular.utils';
import { uuid } from '@ppl/utils/crypto.utils';
import { markFormGroupTouched } from '@ppl/utils/form-control.utils';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import type { PplFormPopoverActionsComponent } from '../form-popover-actions/form-popover-actions.component';

@Component({
  selector: 'ppl-form-group',
  templateUrl: './form-group.component.html',
  styleUrls: ['./form-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PplFormGroupComponent implements OnChanges, OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() transparent = false;
  @Input() noPadding = false;
  @Input() saving = false;
  @Input() showOverlay = true;
  @Input() formActions?: PplFormActionsComponent | PplFormPopoverActionsComponent;

  @Output() submit = new EventEmitter();
  @Output() cancel = new EventEmitter();

  @ContentChild(PplFormActionsComponent, { static: false }) childFormActions: PplFormActionsComponent;

  id = `form-${uuid()}`;

  markForCheckTimeout: any;

  get actions() {
    return this.formActions || this.childFormActions || null;
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (notFirstChange(changes.saving)) {
      this.propagateSaving();
    }
    if (notFirstChange(changes.formActions)) {
      this.addActionListeners();
    }
  }

  ngOnInit() {
    this.propagateSaving();
    this.addActionListeners();
  }

  ngOnDestroy(): void {
    clearTimeout(this.markForCheckTimeout);
  }

  onSubmit() {
    if (!this.formGroup.valid) {
      markFormGroupTouched(this.formGroup);

      clearTimeout(this.markForCheckTimeout);
      // this has to be here, since the content projected into this one is held by the parent component
      this.markForCheckTimeout = setTimeout(() => this.changeDetectorRef.markForCheck(), 1);
      this.changeDetectorRef.detectChanges();

      setTimeout(() => {
        (this.elementRef.nativeElement as HTMLElement)
          ?.querySelector('.ppl-input-container--error')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      return;
    }

    this.submit.emit(null);
  }

  onCancel() {
    this.cancel.emit(null);
  }

  private propagateSaving() {
    if (this.actions) {
      this.actions.saving = this.saving;
      this.actions.changeDetectorRef.detectChanges();
    }
  }

  private addActionListeners() {
    if (this.actions) {
      this.actions.onSave = () => {
        if (isIE11()) {
          this.onSubmit();
        }
      };
      this.actions.onCancel = () => this.onCancel();
      this.actions.id = this.id;
      this.actions.changeDetectorRef.detectChanges();
    }
  }
}

// TODO: use @ppl/utils
export function isIE11() {
  return !!(window as any).MSInputMethodContext && !!(document as any).documentMode;
}
